import React, {useState, useRef, useEffect} from 'react';
import {Layout} from '../../styles/layout';
import {Text} from '../../styles/text';
import {TextInput} from '../../styles/form';
import {modalTextColor} from '../../utils/variables';

interface CurrencyInputProps {
    value: number;
    onChange: (value: number) => void;
    currency?: string;
    placeholder?: string;
    label?: string;
    disabled?: boolean;
    style?: React.CSSProperties;
    min?: number;
    max?: number;
    onBlur?: () => void;
    name?: string;
}

/**
 * Компонент для ввода денежных значений с форматированием
 * 
 * Особенности:
 * - Принимает целые и дробные значения
 * - Поддерживает "." и "," как десятичный разделитель
 * - Не блокирует ввод во время набора
 * - Форматирует значение только при blur
 * - Отображает валюту рядом с инпутом
 */
const CurrencyInput: React.FC<CurrencyInputProps> = ({
    value,
    onChange,
    currency = '',
    placeholder = '0,00',
    label,
    disabled = false,
    style,
    min,
    max,
    onBlur,
    name
}) => {
    // rawValue - строка, которую видит пользователь во время ввода
    const [rawValue, setRawValue] = useState<string>('');
    // isFocused - флаг для отслеживания фокуса
    const [isFocused, setIsFocused] = useState<boolean>(false);
    // error - сообщение об ошибке
    const [error, setError] = useState<string>('');
    const inputRef = useRef<HTMLInputElement>(null);

    // Инициализация rawValue из value при монтировании или изменении value
    useEffect(() => {
        if (!isFocused) {
            if (value !== undefined && value !== null && !isNaN(value)) {
                const formatted = formatNumber(value);
                setRawValue(formatted);
            } else {
                setRawValue('');
            }
        }
    }, [value, isFocused]);

    /**
     * Форматирует число в строку с разделителями тысяч и 2 знаками после запятой
     * Пример: 1234.5 -> "1 234,50"
     */
    const formatNumber = (num: number): string => {
        if (isNaN(num) || num === null || num === undefined) {
            return '';
        }
        
        // Округляем до 2 знаков после запятой
        const rounded = Math.round(num * 100) / 100;
        
        // Форматируем с локалью ru-RU (разделитель тысяч - пробел, десятичный - запятая)
        return new Intl.NumberFormat('ru-RU', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(rounded);
    };

    /**
     * Нормализует строку в число
     * Заменяет "," на ".", убирает пробелы, парсит в число
     * Поддерживает ввод вида ".5" или ",5"
     */
    const normalizeValue = (str: string): number | null => {
        if (!str || str.trim() === '') {
            return 0;
        }

        // Убираем пробелы (разделители тысяч)
        const withoutSpaces = str.replace(/\s/g, '');
        
        // Заменяем все запятые на точку (на случай множественных запятых)
        const normalized = withoutSpaces.replace(/,/g, '.');
        
        // Если строка начинается с точки или запятой, добавляем 0
        const withLeadingZero = normalized.startsWith('.') ? '0' + normalized : normalized;
        
        // Парсим в число
        const num = parseFloat(withLeadingZero);
        
        if (isNaN(num)) {
            return null;
        }

        return num;
    };

    /**
     * Валидация значения
     */
    const validateValue = (num: number): string => {
        if (min !== undefined && num < min) {
            return `Значение должно быть не менее ${formatNumber(min)}`;
        }
        if (max !== undefined && num > max) {
            return `Значение должно быть не более ${formatNumber(max)}`;
        }
        if (num < 0) {
            return 'Значение не может быть отрицательным';
        }
        return '';
    };

    /**
     * Обработчик изменения значения
     * Не блокирует ввод, просто сохраняет rawValue
     */
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setRawValue(newValue);
        setError(''); // Очищаем ошибку при новом вводе
    };

    /**
     * Обработчик потери фокуса
     * Нормализует значение, валидирует, форматирует и сохраняет
     */
    const handleBlur = () => {
        setIsFocused(false);
        
        const normalized = normalizeValue(rawValue);
        
        if (normalized === null) {
            // Если значение некорректно, показываем ошибку и сбрасываем
            setError('Введите корректное число');
            setRawValue(formatNumber(value || 0));
            if (onBlur) {
                onBlur();
            }
            return;
        }

        // Валидация
        const validationError = validateValue(normalized);
        if (validationError) {
            setError(validationError);
            setRawValue(formatNumber(value || 0));
            if (onBlur) {
                onBlur();
            }
            return;
        }

        // Форматируем и сохраняем
        const formatted = formatNumber(normalized);
        setRawValue(formatted);
        
        // Вызываем onChange с нормализованным числом
        onChange(normalized);
        
        if (onBlur) {
            onBlur();
        }
    };

    /**
     * Обработчик получения фокуса
     * Показываем raw значение без форматирования для удобного редактирования
     */
    const handleFocus = () => {
        setIsFocused(true);
        setError('');
        
        // При фокусе показываем число без форматирования (убираем пробелы)
        // Если значение уже отформатировано, преобразуем его обратно в редактируемый вид
        const normalized = normalizeValue(rawValue);
        if (normalized !== null && normalized !== 0) {
            // Показываем число с запятой, но без пробелов
            // Убираем лишние нули в конце, но оставляем минимум один знак после запятой если есть дробная часть
            const numStr = normalized.toString();
            const parts = numStr.split('.');
            if (parts.length === 2) {
                // Есть дробная часть
                const integerPart = parts[0];
                const decimalPart = parts[1].replace(/0+$/, ''); // Убираем trailing zeros
                const finalDecimal = decimalPart || '0';
                setRawValue(`${integerPart},${finalDecimal}`);
            } else {
                // Целое число
                setRawValue(numStr);
            }
        } else if (rawValue.trim() === '' || rawValue === formatNumber(0)) {
            // Если поле пустое или содержит только 0, очищаем его
            setRawValue('');
        }
    };

    return (
        <Layout vertical style={{width: '100%'}}>
            {label && (
                <Text extent={'S'} color={modalTextColor} style={{marginBottom: '4px'}}>
                    {label}
                </Text>
            )}
            <Layout between={5} vAlign={'center'}>
                <TextInput
                    ref={inputRef}
                    type="text"
                    inputMode="decimal"
                    value={rawValue}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    placeholder={placeholder}
                    disabled={disabled}
                    name={name}
                    style={{
                        width: '100%',
                        flex: 1,
                        ...style
                    }}
                />
                {currency && (
                    <Text 
                        extent={'12px'} 
                        color={modalTextColor} 
                        style={{
                            marginLeft: '8px',
                            whiteSpace: 'nowrap',
                            minWidth: 'fit-content'
                        }}
                    >
                        {currency}
                    </Text>
                )}
            </Layout>
            {error && (
                <Text 
                    extent={'11px'} 
                    color={'#ff4d4f'} 
                    style={{
                        marginTop: '4px',
                        fontStyle: 'italic'
                    }}
                >
                    {error}
                </Text>
            )}
        </Layout>
    );
};

export default CurrencyInput;
